import { useQueryClient } from '@tanstack/react-query';
import FEATURE_FLAGS from 'gcs-common/helper/featureFlagsHelper';
import USER_TYPE from 'gcs-common/helper/userTypeHelper';
import { getSelectedHouseId } from 'gcs-common/slices/houses/housesSelectors';
import { uniqBy } from 'lodash-es';
import { useSelector } from 'react-redux';
import {
  CurrentUserQueryKeys,
  type FetchCurrentUserErpCustomersResponseData,
  type FetchCurrentUserHousesResponseData,
  useGetCurrentUser,
  useGetCurrentUserHouses,
} from './current-user-api';

export const useGetCurrentUserErpCustomersSelector = () => {
  const queryClient = useQueryClient();

  const queries = queryClient.getQueriesData({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_ERP_CUSTOMERS],
  });

  const customersFromQueries = queries.flatMap((query) => {
    const queryData = query[1] as FetchCurrentUserErpCustomersResponseData;
    const customers = queryData ? queryData.data : ([] as FetchCurrentUserErpCustomersResponseData['data']);
    return customers;
  });
  const uniqueCustomers = uniqBy(customersFromQueries, 'id');

  return uniqueCustomers;
};

export const useGetCurrentUserIsAgent = () => {
  const { data: currentUser } = useGetCurrentUser();
  return currentUser?.userType === USER_TYPE.AGENT;
};

export const useGetCurrentUserHasFeatureFlag = (featureFlag: string) => {
  const { data: currentUser, isFetching } = useGetCurrentUser();
  // wait for currentuser to finish loading for consistency if something changes on the BE while fetching
  return !!currentUser?.featureFlags.includes(featureFlag) && !isFetching;
};

export const useGetCurrentUserHouse = (houseId: string) => {
  const queryClient = useQueryClient();
  const queries = queryClient.getQueriesData({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_HOUSES],
  });

  const housesFromQueries = queries.flatMap((query) => {
    const queryData = query[1] as FetchCurrentUserHousesResponseData;
    const houses = queryData ? queryData : ([] as FetchCurrentUserHousesResponseData);
    return houses;
  });

  return housesFromQueries.find((house) => house.id === houseId);
};

export const useGetCurrentUserHouseHasFeatureFlagEnabled = (houseId: string, featureFlag: string) => {
  const { data: houses, isFetching } = useGetCurrentUserHouses();
  return (
    !!houses?.find((h) => h.id === houseId)?.featureFlags?.some((ff) => ff.friendlyName === featureFlag) && !isFetching
  );
};

export const useGetBeamerChatEnabled = () => {
  const isAgent = useGetCurrentUserIsAgent();

  const isBeamerChatEnabledForCurrentUser = useGetCurrentUserHasFeatureFlag(FEATURE_FLAGS.BEAMER_CHAT_ENABLED);

  const selectedHouseId = useSelector(getSelectedHouseId);
  const isBeamerChatEnabledForCurrentHouse = useGetCurrentUserHouseHasFeatureFlagEnabled(
    selectedHouseId,
    FEATURE_FLAGS.BEAMER_CHAT_ENABLED,
  );

  return isAgent ? isBeamerChatEnabledForCurrentUser : isBeamerChatEnabledForCurrentHouse;
};
